<template>
    <dropdown
        :position="position"
        icon="ai-icon"
        iconType="vector"
        :class="classes"
        class="textingAssistant textingAssistant--conversation"
        @icon-click-after="iconClickAfter"
    >
        <div v-if="busy" class="textingAssistant__loader">
            <basic-loader center aria-label="TextingAssistant" />
        </div>
        <div v-else class="textingAssistantContent">
            <div
                v-if="messageLoading && !showAIMessageTollBar"
                class="textingAssistantContent__wrapper"
            >
                <p class="textingAssistantContent__title">Generating text...</p>
            </div>
            <div v-if="showAIMessageTollBar">
                <div class="textingAssistantContent__wrapper">
                    <p v-if="isActiveSuggestResponseFlow" class="textingAssistantContent__title">
                        Suggested response
                    </p>
                    <p
                        v-if="isSuggestAnswer && !isActiveSuggestResponseFlow"
                        class="textingAssistantContent__title"
                    >
                        Suggest a response
                    </p>
                    <p v-if="isAutocorrection" class="textingAssistantContent__title">
                        Rephrase my message
                    </p>
                    <p v-if="isFriendlyToneImprovement" class="textingAssistantContent__title">
                        Make it more friendly
                    </p>
                    <p v-if="isFormalToneImprovement" class="textingAssistantContent__title">
                        Make it more formal
                    </p>
                    <p v-if="isExpand" class="textingAssistantContent__title">Expand my message</p>
                    <p v-if="isShorten" class="textingAssistantContent__title">Make it shorter</p>
                </div>
                <div class="textingAssistantContent__wrapper">
                    <p v-if="messageLoading" class="textingAssistantContent__message">
                        Generating text...
                    </p>
                    <p v-else class="textingAssistantContent__message">{{ paraphrasedMessage }}</p>
                </div>
                <div class="textingAssistantContent__tollBar">
                    <ul class="list">
                        <li class="item">
                            <button
                                class="button"
                                :disabled="messageLoading"
                                @click.prevent="handleApplyMessage"
                            >
                                <fa-icon icon="check" />
                                <span class="text">I like it!</span>
                            </button>
                        </li>
                        <li class="item">
                            <button
                                class="button"
                                :disabled="messageLoading"
                                @click.prevent="handleAgain"
                            >
                                <vector-icon icon="refresh" :size="[15, 16]" :fill="'#7A7A7A'" />
                                <span class="text">Try again</span>
                            </button>
                        </li>
                        <li class="item">
                            <button
                                class="button"
                                :disabled="messageLoading"
                                @click.prevent="reset"
                            >
                                <vector-icon icon="close" :size="[15, 15]" :fill="'#7A7A7A'" />
                                <span class="text">Cancel</span>
                            </button>
                        </li>
                    </ul>
                </div>
            </div>
            <div v-if="showAITollBar">
                <div class="textingAssistantContent__wrapper">
                    <span class="betaLabel">BETA</span>
                    <p class="textingAssistantContent__title">Craft better messages with AI</p>
                </div>
                <div class="textingAssistantContent__tollBar">
                    <ul class="list">
                        <li class="item">
                            <button
                                class="button"
                                :disabled="messageLoading"
                                @click.prevent="handleSuggestResponse"
                            >
                                <vector-icon
                                    icon="lightning-bold"
                                    :size="[15, 16]"
                                    :fill="'#7A7A7A'"
                                />
                                <span class="text">Suggest a response</span>
                            </button>
                        </li>
                        <li class="item">
                            <button
                                class="button"
                                :disabled="isActiveSuggestResponseFlow || messageLoading"
                                @click.prevent="handleRephraseMessage"
                            >
                                <vector-icon icon="refresh" :size="[15, 16]" :fill="'#7A7A7A'" />
                                <span class="text">Rephrase my message</span>
                            </button>
                        </li>
                        <li class="item">
                            <button
                                class="button"
                                :disabled="isActiveSuggestResponseFlow || messageLoading"
                                @click.prevent="handleFriendlyToneImprovementMessage"
                            >
                                <vector-icon
                                    icon="smile-mouth-open"
                                    :size="[15, 15]"
                                    :fill="'#7A7A7A'"
                                />
                                <span class="text">Make it more friendly</span>
                            </button>
                        </li>
                        <li class="item">
                            <button
                                class="button"
                                :disabled="isActiveSuggestResponseFlow || messageLoading"
                                @click.prevent="handleFormalToneImprovementMessage"
                            >
                                <vector-icon
                                    icon="fashion-so-tie"
                                    :size="[15, 15]"
                                    :fill="'#7A7A7A'"
                                />
                                <span class="text">Make it more formal</span>
                            </button>
                        </li>
                        <li class="item">
                            <button
                                class="button"
                                :disabled="isActiveSuggestResponseFlow || messageLoading"
                                @click.prevent="handleExpandMessage"
                            >
                                <vector-icon
                                    icon="expand-outline"
                                    :size="[16, 16]"
                                    :fill="'#7A7A7A'"
                                />
                                <span class="text">Expand my message</span>
                            </button>
                        </li>
                        <li class="item">
                            <button
                                class="button"
                                :disabled="isActiveSuggestResponseFlow || messageLoading"
                                @click.prevent="handleShorterMessage"
                            >
                                <vector-icon
                                    icon="short-outline"
                                    :size="[16, 16]"
                                    :fill="'#7A7A7A'"
                                />
                                <span class="text">Make it shorter</span>
                            </button>
                        </li>
                    </ul>
                </div>
            </div>
        </div>
    </dropdown>
</template>

<script>
import { mapState } from 'pinia';
import stores from 'store/modules';

const ACTION_TYPES = {
    AUTOCORRECTION: 'AUTOCORRECTION',
    EXPAND: 'EXPAND',
    SHORTEN: 'SHORTEN',
    SUGGEST_ANSWER: 'SUGGEST_ANSWER',
    FRIENDLY_TONE_IMPROVEMENT: 'FRIENDLY_TONE_IMPROVEMENT',
    FORMAL_TONE_IMPROVEMENT: 'FORMAL_TONE_IMPROVEMENT',
};

export default {
    emits: ['selected'],

    props: {
        position: { type: String, default: 'left' },
        message: { type: String, default: '' },
        messageClickedPart: { type: String, default: '' },
    },

    data: () => ({
        busy: false,
        isIconClicked: false,
        actionType: null,
    }),

    computed: {
        classes() {
            return {
                'is-busy': this.busy,
                'dropdown-body__open': this.isIconClicked && !this.busy,
            };
        },

        currentMessage() {
            return this.messageClickedPart?.length > 0 ? this.messageClickedPart : this.message;
        },

        isActiveSuggestResponseFlow() {
            return this.isIconClicked && !this.message.trim()?.length;
        },

        isAutocorrection() {
            return this.actionType === ACTION_TYPES.AUTOCORRECTION;
        },

        isExpand() {
            return this.actionType === ACTION_TYPES.EXPAND;
        },

        isShorten() {
            return this.actionType === ACTION_TYPES.SHORTEN;
        },

        isSuggestAnswer() {
            return this.actionType === ACTION_TYPES.SUGGEST_ANSWER;
        },

        isFriendlyToneImprovement() {
            return this.actionType === ACTION_TYPES.FRIENDLY_TONE_IMPROVEMENT;
        },

        isFormalToneImprovement() {
            return this.actionType === ACTION_TYPES.FORMAL_TONE_IMPROVEMENT;
        },

        ...mapState(stores.Dropdown, ['dropdown']),
        ...mapState(stores.TextingAssistant, [
            'showAITollBar',
            'showAIMessageTollBar',
            'paraphrasedMessage',
            'messageLoading',
        ]),
        ...mapState(stores.Messages, ['messagesItemsArray']),
    },

    watch: {
        'dropdown.active'(val) {
            if (val) {
                this.$store.dispatch('changeAITollBarViewStatus', true);
            } else {
                this.isIconClicked = false;
                this.reset();
            }
        },
    },

    methods: {
        iconClickAfter(e) {
            if (e) e.stopPropagation();

            this.isIconClicked = true;
        },

        handleSuggestResponse() {
            this.actionType = ACTION_TYPES.SUGGEST_ANSWER;

            this.$store.dispatch('getConversationsSuggestResponseMessage', {
                messages: this.getMessagesDataList(),
            });
        },

        handleRephraseMessage() {
            this.actionType = ACTION_TYPES.AUTOCORRECTION;
            this.$store.dispatch('getParaphrasedMessage', {
                message: this.currentMessage,
            });
        },

        handleExpandMessage() {
            this.actionType = ACTION_TYPES.EXPAND;
            this.$store.dispatch('getExpandMessage', {
                message: this.currentMessage,
            });
        },

        handleShorterMessage() {
            this.actionType = ACTION_TYPES.SHORTEN;
            this.$store.dispatch('getShortenMessage', {
                message: this.currentMessage,
            });
        },

        handleFriendlyToneImprovementMessage() {
            this.actionType = ACTION_TYPES.FRIENDLY_TONE_IMPROVEMENT;
            this.$store.dispatch('getToneImprovementMessage', {
                message: this.currentMessage,
                toneType: 'friendly',
            });
        },

        handleFormalToneImprovementMessage() {
            this.actionType = ACTION_TYPES.FORMAL_TONE_IMPROVEMENT;
            this.$store.dispatch('getToneImprovementMessage', {
                message: this.currentMessage,
                toneType: 'formal',
            });
        },

        handleAgain() {
            const params = {
                message: this.currentMessage,
                isTryAgain: true,
            };

            const paramsTone = {
                message: this.currentMessage,
                isTryAgain: true,
                toneType: this.isFormalToneImprovement ? 'formal' : 'friendly',
            };

            const paramsActiveSuggestResponseFlow = {
                messages: this.getMessagesDataList(),
                isTryAgain: true,
            };

            if (this.isSuggestAnswer)
                this.$store.dispatch(
                    'getConversationsSuggestResponseMessage',
                    paramsActiveSuggestResponseFlow
                );
            if (this.isAutocorrection) this.$store.dispatch('getParaphrasedMessage', params);
            if (this.isExpand) this.$store.dispatch('getExpandMessage', params);
            if (this.isShorten) this.$store.dispatch('getShortenMessage', params);
            if (this.isFormalToneImprovement || this.isFriendlyToneImprovement) {
                this.$store.dispatch('getToneImprovementMessage', paramsTone);
            }
        },

        handleApplyMessage() {
            if (this.messageClickedPart?.length > 0) {
                this.$emit('selected', this.paraphrasedMessage);
                this.reset();
            } else {
                this.$emit('selected', this.paraphrasedMessage, true, true);
                this.reset();
            }
        },

        async reset() {
            this.busy = false;
            this.isIconClicked = false;
            this.actionType = null;

            await this.$delay(50);
            await this.$store.dispatch('resetAI');
            await this.$store.dispatch('dropdownClose');
        },

        getMessagesDataList() {
            const messagesList = [];
            const MAX_MESSAGES_COUNT = 50;

            this.messagesItemsArray.forEach((currentMessage, index) => {
                if (index < MAX_MESSAGES_COUNT && ['mms', 'sms'].includes(currentMessage.type)) {
                    messagesList.push({
                        direction: currentMessage.isReceivedType ? 'inbound' : 'outbound',
                        first_name: currentMessage.user.first_name,
                        message: currentMessage.body.toString().replace(/(<([^>]+)>)/gi, ''),
                    });
                }
            });

            return messagesList;
        },
    },

    unmounted() {
        this.reset();
    },

    mounted() {
        this.reset();
    },
};
</script>
